// src/stock/Categories.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Table, Row, Col, Form, Button,
    Spinner, Modal, InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Categories() {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedCategorie, setSelectedCategorie] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [categorieToDelete, setCategorieToDelete] = useState(null);

    const [formData, setFormData] = useState({
        code_categorie: '',
        nom_categorie: '',
        description: ''
    });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchCategories();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchCategories = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/categories.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setCategories(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.code_categorie || !formData.nom_categorie) {
            showToast('error', 'Champs requis', "Le code et le nom de la catégorie sont requis");
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/categories.php`;
            let method = 'POST';
            if (modalMode === 'edit') {
                url += `?id=${selectedCategorie.id_categorie}`;
                method = 'PUT';
            }
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
            const result = await response.json();
            if (result.success) {
                setShowModal(false);
                fetchCategories();
                showToast('success', 'Succès',
                    modalMode === 'add'
                        ? 'Catégorie créée avec succès'
                        : 'Catégorie mise à jour avec succès'
                );
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (id, nom, code) => {
        setCategorieToDelete({ id, nom, code });
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!categorieToDelete) return;
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/categories.php?id=${categorieToDelete.id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const result = await response.json();
            if (result.success) {
                setShowDeleteConfirm(false);
                setCategorieToDelete(null);
                fetchCategories();
                showToast('success', 'Succès', "Catégorie supprimée avec succès");
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la suppression");
                setShowDeleteConfirm(false);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur de connexion");
            setShowDeleteConfirm(false);
        }
    };

    const handleEdit = (categorie) => {
        setSelectedCategorie(categorie);
        setFormData({
            code_categorie: categorie.code_categorie,
            nom_categorie: categorie.nom_categorie,
            description: categorie.description || ''
        });
        setModalMode('edit');
        setShowModal(true);
    };

    const handleView = (categorie) => {
        setSelectedCategorie(categorie);
        setModalMode('view');
        setShowModal(true);
    };

    const handleAdd = () => {
        setFormData({
            code_categorie: '',
            nom_categorie: '',
            description: ''
        });
        setModalMode('add');
        setShowModal(true);
    };

    /* ============================================================
       FILTER
       ============================================================ */
    const filteredCategories = categories.filter(c =>
        c.code_categorie?.toLowerCase().includes(filterText.toLowerCase()) ||
        c.nom_categorie?.toLowerCase().includes(filterText.toLowerCase()) ||
        c.description?.toLowerCase().includes(filterText.toLowerCase())
    );

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading && !categories.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des catégories...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-price-tag-3-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Gestion des catégories</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {filteredCategories.length}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchCategories} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={handleAdd}>
                                <i className="ri-add-line"></i> Nouvelle catégorie
                            </Button>
                        </div>
                    </div>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="8">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher catégorie..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="4" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {filteredCategories.length} catégorie(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-price-tag-3-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des catégories</span>
                                    <span className="badge-header-light">{filteredCategories.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Code</th>
                                            <th>Nom</th>
                                            <th>Description</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5">
                                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                                </td>
                                            </tr>
                                        ) : filteredCategories.length > 0 ? (
                                            filteredCategories.map((categorie) => (
                                                <tr key={categorie.id_categorie}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge-charte">
                                                            <i className="ri-barcode-line me-1"></i>
                                                            {categorie.code_categorie}
                                                        </span>
                                                    </td>
                                                    <td className="fw-semibold">{categorie.nom_categorie}</td>
                                                    <td className="text-muted">{categorie.description || '—'}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <button
                                                                className="action-btn-charte"
                                                                onClick={() => handleView(categorie)}
                                                                style={{ '--btn-color': COLORS.primary }}
                                                                title="Voir détails"
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </button>
                                                            <button
                                                                className="action-btn-charte"
                                                                onClick={() => handleEdit(categorie)}
                                                                style={{ '--btn-color': COLORS.primary }}
                                                                title="Modifier"
                                                            >
                                                                <i className="ri-pencil-line"></i>
                                                            </button>
                                                            <button
                                                                className="action-btn-charte"
                                                                onClick={() => handleDeleteClick(categorie.id_categorie, categorie.nom_categorie, categorie.code_categorie)}
                                                                style={{ '--btn-color': COLORS.danger }}
                                                                title="Supprimer"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-price-tag-3-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucune catégorie trouvée</h6>
                                                        <p className="text-muted small mb-0">
                                                            {filterText
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par créer votre première catégorie"}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL CATÉGORIE ===== */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered className="modal-charte">
                <Modal.Header
                    closeButton
                    className="modal-header-charte"
                    style={{
                        background: modalMode === 'view'
                            ? `linear-gradient(135deg, ${COLORS.info}, #2563eb)`
                            : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-price-tag-3-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">
                                {modalMode === 'add' && 'Nouvelle catégorie'}
                                {modalMode === 'edit' && 'Modifier la catégorie'}
                                {modalMode === 'view' && 'Détails de la catégorie'}
                            </Modal.Title>
                            <p className="text-white-50 mb-0 small">
                                {modalMode === 'view' ? 'Consultation' : 'Remplissez les informations'}
                            </p>
                        </div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="p-4">
                        {modalMode === 'view' && selectedCategorie ? (
                            <div className="text-center py-3">
                                <div className="view-icon-charte mx-auto mb-3">
                                    <i className="ri-price-tag-3-line"></i>
                                </div>
                                <h4 className="fw-bold">{selectedCategorie.nom_categorie}</h4>
                                <div className="mt-3">
                                    <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                                        <i className="ri-barcode-line"></i>
                                        Code: {selectedCategorie.code_categorie}
                                    </span>
                                </div>
                                {selectedCategorie.description && (
                                    <div className="summary-card-charte mt-3 text-start">
                                        <small className="text-muted d-block mb-1">Description</small>
                                        <p className="mb-0">{selectedCategorie.description}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="form-label-modern">
                                            Code <span className="text-danger">*</span>
                                        </Form.Label>
                                        <InputGroup className="input-modern">
                                            <InputGroup.Text><i className="ri-barcode-line"></i></InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                value={formData.code_categorie}
                                                onChange={(e) => setFormData({ ...formData, code_categorie: e.target.value.toUpperCase() })}
                                                disabled={modalMode === 'edit'}
                                                required
                                                placeholder="Ex: ELEC"
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="form-label-modern">
                                            Nom <span className="text-danger">*</span>
                                        </Form.Label>
                                        <InputGroup className="input-modern">
                                            <InputGroup.Text><i className="ri-text"></i></InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                value={formData.nom_categorie}
                                                onChange={(e) => setFormData({ ...formData, nom_categorie: e.target.value })}
                                                required
                                                placeholder="Ex: Électronique"
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="form-label-modern">Description</Form.Label>
                                        <InputGroup className="input-modern">
                                            <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Description de la catégorie..."
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowModal(false)} type="button">
                            Fermer
                        </Button>
                        {modalMode !== 'view' && (
                            <Button className="btn-modern-primary" type="submit" disabled={saving}>
                                {saving ? (
                                    <><Spinner size="sm" className="me-2" />Enregistrement...</>
                                ) : (
                                    <>
                                        <i className="ri-save-line me-2"></i>
                                        {modalMode === 'add' ? 'Créer' : 'Mettre à jour'}
                                    </>
                                )}
                            </Button>
                        )}
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ===== MODAL SUPPRESSION ===== */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la suppression</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-center text-muted mb-3">
                        Êtes-vous sûr de vouloir supprimer la catégorie ?
                    </p>
                    <div className="summary-card-charte mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="categorie-avatar-charte">
                                <i className="ri-price-tag-3-line"></i>
                            </div>
                            <div>
                                <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                    {categorieToDelete?.nom}
                                </div>
                                <div className="text-muted small">
                                    <i className="ri-barcode-line me-1"></i>
                                    Code: {categorieToDelete?.code}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="alert-error-charte mb-0">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                            <div className="ms-3">
                                <strong>Attention</strong>
                                <p className="mb-0 small">Cette action est irréversible</p>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button className="btn-modern-outline" onClick={() => setShowDeleteConfirm(false)}>
                        Annuler
                    </Button>
                    <Button className="btn-modern-danger" onClick={confirmDelete}>
                        <i className="ri-delete-bin-line me-2"></i>Confirmer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }

                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== VIEW ICON ===== */
                .view-icon-charte {
                    width: 80px; height: 80px;
                    background: ${COLORS.primary}15;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--primary); font-size: 2rem;
                }

                /* ===== STATUS BADGE ===== */
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.85rem;
                    border-radius: 50px; font-size: 0.75rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== FORM ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .categorie-avatar-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 1.2rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}